import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenshotStore } from '../stores/screenshotStore';
import ReportIssuePage from '../modules/utility/ds-helpdesk/pages/ReportIssuePage';

/**
 * Global "New Ticket" popup host.
 *
 * Renders the Report Issue form (modal mode) as a fixed overlay on whatever page
 * the user is on. Controlled by the shared screenshot store's `ticketModalOpen`
 * flag, so the floating Report Issue button — which captures a screenshot and
 * then opens this — can raise a ticket from ANY screen without navigating away.
 * Mounted once globally in the app Layout.
 *
 * Rendered through a portal to document.body so the fixed overlay is centered on
 * the viewport and never clipped/offset by the (sidebar-shifted) content area.
 */
const GlobalNewTicketModal: React.FC = () => {
  const isOpen = useScreenshotStore((s) => s.ticketModalOpen);
  const closeTicketModal = useScreenshotStore((s) => s.closeTicketModal);
  const [isFullscreen, setIsFullscreen] = useState(false);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 w-screen h-screen z-[9000] overflow-hidden" data-html2canvas-ignore="true">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={closeTicketModal}></div>
      <div className={`relative flex min-h-full w-full justify-center overflow-y-auto ${isFullscreen ? 'p-0 items-stretch' : 'p-4 items-start'}`}>
        <div className={`relative bg-gray-50 shadow-2xl w-full flex flex-col overflow-hidden ${isFullscreen ? 'max-w-none h-screen rounded-none' : 'max-w-5xl my-6 max-h-[90vh] rounded-xl'}`}>
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 min-h-[70px] sm:min-h-[80px] bg-indigo-600">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={closeTicketModal}
                title="Back"
                aria-label="Back"
                className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-white leading-tight truncate">New Ticket</h1>
                <p className="text-xs sm:text-sm text-indigo-100 leading-tight truncate">Raise a new support ticket</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsFullscreen((f) => !f)}
                title={isFullscreen ? 'Exit full screen' : 'Full screen'}
                aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                className="w-8 h-8 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <i className={isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}></i>
              </button>
              <button
                type="button"
                onClick={closeTicketModal}
                title="Close"
                aria-label="Close"
                className="w-8 h-8 flex items-center justify-center rounded-md bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <ReportIssuePage
              isModal
              onClose={closeTicketModal}
              onCreated={closeTicketModal}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GlobalNewTicketModal;
